import { SITE_NAME, SITE_PRIMARY_COLOR } from "@api/constants";
import fetchCourseCompletionGroups from "@api/operations/fetch-course-completion-groups";
import fetchUserState from "@api/operations/fetch-customer-state";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { NextSeo } from "next-seo";
import Router, { useRouter } from "next/router";
import React, { useEffect } from "react";

import Center from "@components/Center";
import Container from "@components/Container";
import EnrolledCourseGroups from "@components/EnrolledCourseGroups";
import MainLayout from "@components/MainLayout";
import PaginationWithResultsCount from "@components/PaginationWithResultsCount";
import Spacer from "@components/Spacer";
import Title from "@components/Title";

export const getServerSideProps: GetServerSideProps = async ({ req, query }) => {
  const {
    cookies: { token },
  } = req;
  const { page, tab } = query;

  // token may not exist yet if you have just accessed the site supply a mock customer in this case
  if (!token) {
    return {
      props: {
        customer: { guest: true },
        completions: [],
      },
    };
  }

  const finalTab = (tab as string) ?? "active";

  const { customer } = await fetchUserState(token);
  const { completionGroupPage } = await fetchCourseCompletionGroups(
    token,
    [
      { code: "isEvent", namespace: "attribute", value: "false" }, // no events
      {
        code: "enabled",
        namespace: "attribute",
        value: finalTab === "expired" ? "false" : "true",
      },
    ],
    {
      page: page ? parseInt(page as string) - 1 : 0,
      size: 5,
      direction: "DESC",
      sort: "lastActivity",
    }
  );

  return {
    props: {
      completionGroupPage,
      customer,
      tab: finalTab,
    },
  };
};

type CoursePageProps = InferGetServerSidePropsType<typeof getServerSideProps>;

type CourseTab = "active" | "expired";

export default function MyCoursesPage({ customer, completionGroupPage, tab }: CoursePageProps) {
  const router = useRouter();
  const reloadOnFocus = () => {
    if (document.visibilityState === "visible") {
      router.reload();
    }
  };

  const onTabChange = (newTab: CourseTab) => {
    router.push(`?tab=${newTab}`, undefined, { shallow: false }).then((_r) => {});
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", reloadOnFocus);
    return () => {
      return document.removeEventListener("visibilitychange", reloadOnFocus);
    };
  });

  return (
    <Container>
      <NextSeo title={`My Courses | ${SITE_NAME}`} />
      <Title>My Courses</Title>
      <Spacer size={"md"} />
      {customer.guest ? (
        <Center cy="login-required">
          Please{" "}
          <a href={"/login"} style={{ fontWeight: "bold", textDecoration: "underline" }}>
            log in
          </a>{" "}
          before attempting to access your courses.
        </Center>
      ) : (
        <>
          <div className="tabs">
            <div className="tabs-container">
              <nav className="tabs-nav">
                <a
                  href="#"
                  onClick={() => onTabChange("active")}
                  className={tab === "active" ? "active-tab" : "tab"}>
                  Available Courses
                </a>
                <a
                  href="#"
                  onClick={() => onTabChange("expired")}
                  className={tab === "expired" ? "active-tab" : "tab"}>
                  Inactive Courses
                </a>
              </nav>
            </div>
          </div>
          {completionGroupPage.totalElements > 0 ? (
            <>
              <EnrolledCourseGroups enrollmentGroups={completionGroupPage.content} />
              {completionGroupPage && completionGroupPage.totalPages > 1 && (
                <>
                  <Spacer size="md" />
                  <PaginationWithResultsCount
                    perPage={completionGroupPage.size}
                    itemCount={parseInt(completionGroupPage.totalElements)}
                    page={completionGroupPage.page}
                    onPageChange={(value: number) => {
                      Router.push(`${Router.pathname}?page=${value + 1}`).then((_r) => {});
                    }}
                  />
                </>
              )}
            </>
          ) : (
            <Center cy="no-courses">
              <Spacer size="md" />
              {tab === "active" ? (
                <span>
                  You are not currently enrolled in any online courses. Please visit the{" "}
                  <a href={"/course/catalog"} style={{ fontWeight: "bold", textDecoration: "underline" }}>
                    Course Catalog
                  </a>{" "}
                  page to view current course offerings.
                </span>
              ) : (
                <span>There are no inactive courses.</span>
              )}
            </Center>
          )}
        </>
      )}
      <style jsx>{`
        .tabs {
          display: block;
          margin-bottom: 1.5rem;
        }

        .tabs-container {
          border-bottom: 1px solid rgb(229, 231, 235);
        }

        .tabs-nav {
          display: flex;
          margin-bottom: -1px;
        }

        .tabs-nav > :not([hidden]) ~ :not([hidden]) {
          margin-right: 0;
          margin-left: 2rem;
        }

        .tab {
          color: rgb(107, 114, 128);
          font-weight: 500;
          font-size: 0.875rem;
          line-height: 1.25rem;
          padding: 1rem 0.25rem;
          border-bottom: 2px solid transparent;
          white-space: nowrap;
          text-decoration: none;
        }

        .tab:hover {
          color: rgb(55, 65, 81);
          border-color: rgb(209, 213, 219);
        }

        .active-tab {
          color: ${SITE_PRIMARY_COLOR};
          font-weight: 500;
          font-size: 0.875rem;
          line-height: 1.25rem;
          padding: 1rem 0.25rem;
          border-bottom: 2px solid ${SITE_PRIMARY_COLOR};
          white-space: nowrap;
          text-decoration: none;
        }
      `}</style>
    </Container>
  );
}

MyCoursesPage.Layout = MainLayout;
